import type { FlywheelArticle } from "@/lib/flywheel";
import type { BlogPostMeta } from "@/content/blog/types";
import type { LearnTopicMeta } from "@/content/learn/types";

/** Keep the original date string while the stored instant is unchanged, so dates render identically. */
export function keepDate(original: unknown, stored: string | null | undefined): string {
  if (!stored) return typeof original === "string" ? original : "";
  return typeof original === "string" && Date.parse(original) === Date.parse(stored) ? original : stored;
}

/** Blog-post layout meta rebuilt from a migrated database row; database columns win over the saved page data. */
export function storedPostMeta(a: FlywheelArticle): BlogPostMeta {
  const p = a.page_data as unknown as BlogPostMeta;
  const date = keepDate(p.date, a.published_at ?? a.created_at);
  const modified = keepDate(p.dateModified ?? p.date, a.updated_at);
  return {
    ...p, slug: a.slug, title: a.meta_title, h1: a.title === a.meta_title ? undefined : a.title,
    description: a.meta_description, category: a.category, date,
    dateModified: modified === date && p.dateModified === undefined ? undefined : modified,
    faq: a.faq.length || p.faq ? a.faq.map((f) => ({ q: f.question, a: f.answer })) : undefined,
  };
}

/** Guide layout meta rebuilt from a migrated database row. */
export type StoredTopic = Omit<LearnTopicMeta, "Body"> & { published: string };
export function storedTopicMeta(a: FlywheelArticle): StoredTopic {
  const p = a.page_data as unknown as Omit<LearnTopicMeta, "Body">;
  return {
    ...p, title: a.meta_title, h1: a.title === p.keyword ? undefined : a.title,
    description: a.meta_description, faq: a.faq.map((f) => ({ q: f.question, a: f.answer })),
    updated: keepDate(p.updated, a.updated_at), published: keepDate(p.updated, a.published_at ?? a.created_at),
  };
}

/** Click delegation for the contextual offer link inside stored article HTML. */
export function storedCtaClick(pageSlug: string, destination: string) {
  return (e: { target: EventTarget | null }) => {
    const el = e.target as Element | null;
    if (!el || typeof el.closest !== "function" || !el.closest('aside[aria-label="Try LeadXkids"] a')) return;
    window.dispatchEvent(new CustomEvent("lx:organic-trial-cta", { detail: { page_slug: pageSlug, placement: "contextual", destination } }));
  };
}
